import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import type { EditVoiceCloneConfig } from './voice-cloning-operations';
import { editVoiceClone } from './voice-cloning-operations';
import VoiceCloningConfigureStep from './VoiceCloningConfigureStep';

import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import { getAIVoiceModalVoiceById } from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-selectors';
import { upsertAIVoice } from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-slice';
import type { VoiceEditModalContext } from 'js/src/data/modals';
import {
    getModalContext,
    hideModal,
    isVoiceEditModalShowing,
    MODAL_IDS,
    showModal,
} from 'js/src/data/modals';
import { ImmutableFromJS } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import { useAppDispatch } from 'js/src/libs/RootState';

const VoiceEditModal = () => {
    const dispatch = useAppDispatch();
    const isShowing = useSelector(isVoiceEditModalShowing);
    const modalContext = useSelector(getModalContext<VoiceEditModalContext>);
    const voiceSlug = modalContext?.get('voiceSlug') ?? null;
    const returnToVoiceSelection = modalContext?.toJS()['returnToVoiceSelection'];

    const voice = useSelector((state: RootState) => {
        if (!voiceSlug) return null;
        return getAIVoiceModalVoiceById(state, voiceSlug);
    });

    const [isSaving, setIsSaving] = useState(false);

    const handleClose = useCallback(() => {
        dispatch(hideModal());
        if (returnToVoiceSelection) {
            dispatch(
                showModal({
                    modal_id: MODAL_IDS.AI_VOICE_SELECTION,
                    modal_context: ImmutableFromJS(returnToVoiceSelection),
                }),
            );
        }
    }, [dispatch, returnToVoiceSelection]);

    const handleSave = useCallback(
        async (config: EditVoiceCloneConfig) => {
            if (!voiceSlug || isSaving) return;
            setIsSaving(true);
            try {
                const updated = await editVoiceClone(voiceSlug, config);
                if (updated) {
                    dispatch(upsertAIVoice(updated));
                    handleClose();
                }
            } finally {
                setIsSaving(false);
            }
        },
        [voiceSlug, isSaving, handleClose, dispatch],
    );

    return (
        <Modal
            title="Edit AI voice"
            size={MODAL_SIZES.LG}
            showing={isShowing}
            onHidden={handleClose}
            bodyClassName="py-l5-40"
        >
            {voice && (
                <VoiceCloningConfigureStep
                    key={voiceSlug}
                    clonedVoice={voice}
                    cloneError={null}
                    isSaving={isSaving}
                    onSave={handleSave}
                    onBack={handleClose}
                    secondaryLabel="Cancel"
                    initialVoiceName={voice.display_name}
                    initialLanguageDisplay={voice.attributes.language?.[0]}
                    initialStyleDisplay={voice.attributes.style?.[0]}
                    initialToneDisplay={voice.attributes.tone?.[0]}
                    initialGenderDisplay={voice.attributes.gender?.[0]}
                    initialAgeDisplay={voice.attributes.age?.[0]}
                />
            )}
        </Modal>
    );
};

export default VoiceEditModal;
